import React, { useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Users, Zap, Shield, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: Target,
      title: 'Fokus på live events',
      description:
        'Vi bygger software med udgangspunkt i den virkelige drift på festivaler, venues og events.',
    },
    {
      icon: Users,
      title: 'Partnerdrevet tilgang',
      description:
        'Vi arbejder tæt sammen med arrangører og teknologipartnere for at skabe løsninger, der fungerer i praksis.',
    },
    {
      icon: Zap,
      title: 'Mere end bare betaling',
      description:
        'MUSEIQ samler betaling, drift og data i én platform, så arrangører får færre systemer og bedre overblik.',
    },
    {
      icon: Shield,
      title: 'Software i centrum',
      description:
        'Vi er en softwarevirksomhed og samarbejder med specialiserede hardwarepartnere, når det giver mening.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-white via-secondary/30 to-accent-blue/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight">
                Om MUSEIQ
              </h1>
              <p className="text-xl text-body max-w-3xl mx-auto leading-relaxed">
                MUSEIQ er en event operations platform, der hjælper festivaler,
                venues og live events med at samle betaling, drift og data i ét
                system.
              </p>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                  Vi bygger software til live events
                </h2>
                <p className="text-lg text-body leading-relaxed">
                  Vi tror på, at eventdrift skal være enklere. For mange
                  arrangører betyder events stadig lange køer, manuelle
                  processer og flere systemer, der ikke taler sammen. MUSEIQ er
                  bygget for at gøre den virkelighed mere overskuelig.
                </p>
              </div>

              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                  Mere end et POS-system
                </h2>
                <p className="text-lg text-body leading-relaxed">
                  MUSEIQ handler ikke kun om betalinger. Platformen hjælper
                  arrangører med at planlægge, afvikle og analysere events – fra
                  frivillige og lager til Fast Track, mobilbestilling og live
                  overblik.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-secondary/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">
                Hvad der kendetegner MUSEIQ
              </h2>
              <p className="text-lg text-body max-w-2xl mx-auto">
                Vi bygger løsninger til den virkelige drift – når trykket er
                størst, og overblikket betyder mest.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <Card
                    key={index}
                    className="border-0 shadow-lg rounded-3xl bg-white"
                  >
                    <CardContent className="p-8">
                      <div className="w-14 h-14 rounded-2xl bg-accent-blue/10 flex items-center justify-center mb-6">
                        <Icon className="w-7 h-7 text-accent-blue" />
                      </div>
                      <h3 className="text-xl font-bold text-primary mb-4">
                        {value.title}
                      </h3>
                      <p className="text-body leading-relaxed">
                        {value.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Software + partners */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                  Software i centrum
                </h2>
                <p className="text-lg text-body leading-relaxed">
                  MUSEIQ er en softwarevirksomhed. Når der er behov for
                  hardware, samarbejder vi med specialiserede partnere som S5 og
                  PAX, så kunderne får en løsning, hvor software og hardware
                  spiller sammen.
                </p>
              </div>

              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                  Bygget til den virkelige drift
                </h2>
                <p className="text-lg text-body leading-relaxed">
                  Platformen er skabt med fokus på live events, hvor presset er
                  størst i korte perioder. Derfor er MUSEIQ udviklet til at
                  fungere i praksis – når barerne åbner, køerne vokser, og
                  driften skal spille.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section-surface py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Vil du se hvordan MUSEIQ fungerer i praksis?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Book en demo og få en gennemgang af platformen.
            </p>
            <Button
              size="lg"
              className="cta-button-neutral font-semibold px-8 py-4 rounded-xl"
              onClick={() => {
                const subject = encodeURIComponent("Anmodning om demo - MUSEIQ");
                const body = encodeURIComponent("Hej MuseIQ\n\nJeg vil gerne booke en demo af jeres løsning.\n\nI må meget gerne kontakte mig for at aftale et tidspunkt.\n\nBedste hilsner\n[Navn]\n[Virksomhed]");
                window.location.href = `mailto:Kasper@museiq.io?subject=${subject}&body=${body}`;
              }}
            >
              Book en demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
